import { useState } from 'react';
import { Book } from '../../types';
import { useStore } from '../../store/useStore';
import {
  FiArrowLeft,
  FiList,
  FiSettings,
  FiBookmark,
  FiSearch,
  FiVolume2,
  FiVolumeX,
  FiMaximize,
  FiPrinter,
  FiFileText,
  FiEdit3,
  FiShare2,
  FiMusic,
  FiGrid,
  FiDownload,
} from 'react-icons/fi';

type ViewMode = 'scroll' | 'flipbook';

interface ReaderHeaderProps {
  book: Book;
  currentLocation: string;
  onToggleHighlights: () => void;
  showHighlights: boolean;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  onToggleSearch?: () => void;
  showSearch?: boolean;
  onToggleShare?: () => void;
  onToggleBookmarks?: () => void;
  showBookmarks?: boolean;
  onToggleThumbnails?: () => void;
  showThumbnails?: boolean;
  onToggleAudio?: () => void;
  showAudio?: boolean;
}

const ReaderHeader = ({ book, onToggleHighlights, showHighlights, viewMode = 'scroll', onViewModeChange, onToggleSearch, showSearch = false, onToggleShare, onToggleBookmarks, showBookmarks = false, onToggleThumbnails, showThumbnails = false, onToggleAudio, showAudio = false }: ReaderHeaderProps) => {
  const { setCurrentView, setCurrentBook, toggleTOC, toggleSettings, showTOC, showSettings } = useStore();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechRate, setSpeechRate] = useState(1.0);
  const [showRateMenu, setShowRateMenu] = useState(false);

  const handleBack = () => {
    if (isSpeaking) {
      window.speechSynthesis?.cancel();
      setIsSpeaking(false);
    }
    setCurrentBook(null);
    setCurrentView('library');
  };

  // 🔊 TTS - EPUB + PDF 통합 지원
  const handleTTS = async () => {
    if (!('speechSynthesis' in window)) {
      alert('이 브라우저는 음성 합성을 지원하지 않습니다.');
      return;
    }

    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    let text = '';
    const isPDF = book.fileType === 'pdf';

    // S2-3(2026-09-14): 책넘김 모드에선 Reader.tsx가 등록한 창구로 "지금 펼쳐진 쪽"의
    // 텍스트를 받는다(스프레드면 두 쪽 모두). PDFViewer/EPUBViewer는 이 모드에서
    // 마운트되지 않으므로 아래 pdf/epubGetCurrentText는 어차피 비어 있다.
    if (viewMode === 'flipbook' && window.flipbookGetCurrentText) {
      text = await window.flipbookGetCurrentText();
      console.log('📖 책넘김 TTS 텍스트:', text.slice(0, 100) + '...');
    } else if (isPDF && window.pdfGetCurrentText) {
      text = await window.pdfGetCurrentText();
      console.log('📖 PDF TTS 텍스트:', text.slice(0, 100) + '...');
    } else if (window.epubGetCurrentText) {
      text = window.epubGetCurrentText();
      console.log('📖 EPUB TTS 텍스트:', text.slice(0, 100) + '...');
    }
    
    // fallback
    if (!text) {
      const viewer = document.querySelector('.epub-viewer, canvas');
      if (viewer && viewer.tagName !== 'CANVAS') {
        const iframe = viewer.querySelector('iframe');
        if (iframe?.contentDocument?.body) {
          text = iframe.contentDocument.body.innerText || '';
        }
      }
    }

    if (!text.trim()) {
      alert('읽을 텍스트가 없습니다. 페이지를 이동해보세요.');
      return;
    }

    text = text.replace(/\s+/g, ' ').replace(/\n+/g, '. ').trim().slice(0, 5000);

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ko-KR';
    utterance.rate = speechRate;
    
    const voices = window.speechSynthesis.getVoices();
    const koreanVoice = voices.find(v => v.lang.startsWith('ko'));
    if (koreanVoice) utterance.voice = koreanVoice;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  };

  const handlePrint = () => window.print();
  const handleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen();
    else document.exitFullscreen();
  };

  const rateOptions = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-2 py-2">
      <div className="flex items-center justify-between">
        {/* 좌측: 라이브러리 + 파일명 + 보기모드 */}
        <div className="flex items-center gap-1">
          <button onClick={handleBack} className="flex items-center gap-1.5 px-2 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="책장으로">
            <FiArrowLeft size={16} />
            <span className="hidden lg:inline">라이브러리</span>
          </button>

          {/* 파일명 (모바일 숨김) */}
          <div className="hidden md:flex items-center gap-1 px-2 max-w-[150px]">
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {book.title}
            </span>
          </div>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

          {/* ✅ 보기 모드 토글 (통합) — V6(현장보고 2026-08-26): 책넘김을 먼저 배치.
              이 서비스에 오는 사람 다수가 플립북을 보러 오는데 순서·기본값이 반대였다. */}
          {onViewModeChange && (
            <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              <button
                onClick={() => onViewModeChange('flipbook')}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-all ${
                  viewMode === 'flipbook'
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                }`}
                title="책넘김 모드"
              >
                <FiBookmark size={14} />
                <span className="hidden sm:inline">책넘김</span>
              </button>
              <button
                onClick={() => onViewModeChange('scroll')}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-all ${
                  viewMode === 'scroll'
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                }`}
                title="스크롤 모드"
              >
                <FiList size={14} />
                <span className="hidden sm:inline">스크롤</span>
              </button>
            </div>
          )}
        </div>

        {/* 중앙: 도구들 */}
        <div className="flex items-center gap-0.5">
          <button onClick={toggleTOC} className={`p-2 rounded-lg transition-colors ${showTOC ? 'bg-blue-100 dark:bg-blue-900 text-blue-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`} title="목차">
            <FiFileText size={16} />
          </button>

          <button onClick={onToggleHighlights} className={`p-2 rounded-lg transition-colors ${showHighlights ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`} title="메모">
            <FiEdit3 size={16} />
          </button>

          <button
            onClick={onToggleBookmarks}
            className={`p-2 rounded-lg transition-colors ${showBookmarks ? 'bg-red-100 dark:bg-red-900 text-red-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="북마크"
          >
            <FiBookmark size={16} />
          </button>

          <button
            onClick={onToggleThumbnails}
            className={`p-2 rounded-lg transition-colors ${showThumbnails ? 'bg-purple-100 dark:bg-purple-900 text-purple-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="썸네일 네비게이션"
          >
            <FiGrid size={16} />
          </button>

          <button
            onClick={onToggleAudio}
            className={`p-2 rounded-lg transition-colors ${showAudio ? 'bg-pink-100 dark:bg-pink-900 text-pink-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="배경 음악"
          >
            <FiMusic size={16} />
          </button>

          <button
            onClick={onToggleSearch}
            className={`p-2 rounded-lg transition-colors ${showSearch ? 'bg-green-100 dark:bg-green-900 text-green-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="검색 (Ctrl+F)"
          >
            <FiSearch size={16} />
          </button>
        </div>

        {/* 우측: TTS + 설정 */}
        <div className="flex items-center gap-0.5">
          {/* S2-3(2026-09-14): V5의 임시 조치(책넘김 모드에서 TTS 버튼 비활성화)를 해제한다.
              그때는 PDFViewer/EPUBViewer가 언마운트돼 읽을 텍스트를 구할 창구가 없었지만,
              이제 Reader.tsx가 flipbookGetCurrentText로 "지금 펼쳐진 쪽"의 텍스트를 준다. */}
          <button
            onClick={handleTTS}
            className={`flex items-center gap-1 px-2 py-1.5 text-sm rounded-lg transition-colors ${
              isSpeaking
                ? 'bg-green-100 dark:bg-green-900 text-green-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title={isSpeaking ? '중지' : '소리내어 읽기'}
          >
            {isSpeaking ? <FiVolumeX size={16} /> : <FiVolume2 size={16} />}
          </button>

          <div className="relative">
            <button onClick={() => setShowRateMenu(!showRateMenu)} className="px-1.5 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors font-medium" title="읽기 속도">
              {speechRate.toFixed(1)}x
            </button>
            {showRateMenu && (
              <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50">
                {rateOptions.map((rate) => (
                  <button key={rate} onClick={() => { setSpeechRate(rate); setShowRateMenu(false); }} className={`block w-full px-3 py-1 text-xs text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${speechRate === rate ? 'bg-blue-50 dark:bg-blue-900 text-blue-600' : ''}`}>
                    {rate.toFixed(1)}x
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-0.5" />

          <button onClick={handleFullscreen} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="전체화면">
            <FiMaximize size={16} />
          </button>

          {/* ✅ 공유 버튼 */}
          <button
            onClick={onToggleShare}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="공유하기"
          >
            <FiShare2 size={16} />
          </button>

          {/* P1-12: PDF 다운로드 (무료 책만 — 유료는 PRO 안내) */}
          <button
            onClick={() => {
              // Free books: allow download from IndexedDB
              // Paid books: show PRO upsell
              const isPaid = (book as any).price && (book as any).price > 0;
              if (isPaid) {
                alert('PRO 회원만 다운로드할 수 있습니다.\nUpgrade to PRO to download this book.');
              } else {
                // Trigger file download from IndexedDB
                const downloadBook = async () => {
                  try {
                    const { getEPUBFile, getPDFFile } = await import('../../utils/fileStorage');
                    let blob: Blob | null = null;
                    if (book.fileType === 'pdf') {
                      const ab = await getPDFFile(book.filePath);
                      if (ab) blob = new Blob([ab], { type: 'application/pdf' });
                    } else {
                      blob = await getEPUBFile(book.filePath);
                    }
                    if (blob) {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${book.title}.${book.fileType}`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }
                  } catch (e) {
                    console.error('Download failed:', e);
                  }
                };
                downloadBook();
              }
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="다운로드"
          >
            <FiDownload size={16} />
          </button>

          <button onClick={toggleSettings} className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-gray-200 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`} title="설정">
            <FiSettings size={16} />
          </button>

          <button onClick={handlePrint} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="인쇄">
            <FiPrinter size={16} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default ReaderHeader;
